import { toLocalISODate } from './dateUtils';
// 📊 UTILITIES FOR EXPORTING TO EXCEL
// Location: frontend/src/utils/excelExport.js
// Requires: npm install xlsx

import * as XLSX from 'xlsx';
import nl from '../i18n/nl.js';

const makeT = (language) => (s) => language === 'nl' ? (nl[s] ?? s) : s;

/**
 * Export data to Excel
 * @param {Array} data - Array of objects with the data
 * @param {string} filename - File name (without extension)
 * @param {string} sheetName - Sheet name
 */
export const exportToExcel = (data, filename, sheetName = 'Sheet1') => {
  try {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return false;
    }

    // Create worksheet from data
    const worksheet = XLSX.utils.json_to_sheet(data);
    
    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    
    // Auto-adjust column widths
    const maxWidth = 50;
    const colWidths = [];
    
    // Calculate width based on content
    data.forEach(row => {
      Object.keys(row).forEach((key, index) => {
        const value = String(row[key] || '');
        const width = Math.min(Math.max(value.length, key.length) + 2, maxWidth);
        colWidths[index] = Math.max(colWidths[index] || 0, width);
      });
    });
    
    worksheet['!cols'] = colWidths.map(w => ({ wch: w }));
    
    // Generate file with timestamp
    const timestamp = toLocalISODate(new Date());
    XLSX.writeFile(workbook, `${filename}_${timestamp}.xlsx`);
    
    return true;
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return false;
  }
};

/**
 * Export attendance to Excel
 */
export const exportAttendanceToExcel = (attendanceData, language = 'en') => {
  if (!attendanceData || attendanceData.length === 0) {
    return false;
  }

  const t = makeT(language);
  const data = attendanceData.map(record => ({
    [t('Date')]: record.date || '',
    [t('Employee')]: `${record.first_name || ''} ${record.last_name || ''}`.trim(),
    [t('Position')]: record.position || '-',
    [t('Department')]: record.department || '-',
    [t('Clock In')]: record.clock_in || '-',
    [t('Clock Out')]: record.clock_out || '-',
    [t('Hours Worked')]: record.hours_worked || '-',
    [t('Status')]: record.status || '-',
    [t('Notes')]: record.notes || ''
  }));
  
  return exportToExcel(data, 'attendance', t('Attendance'));
};

/**
 * Export payroll to Excel
 */
export const exportPayrollToExcel = (payrollData, language = 'en') => {
  if (!payrollData || payrollData.length === 0) {
    return false;
  }

  const t = makeT(language);
  const data = payrollData.map(record => ({
    [t('Employee')]: `${record.first_name || ''} ${record.last_name || ''}`.trim(),
    [t('Period Start')]: record.period_start || '',
    [t('Period End')]: record.period_end || '',
    [t('Days Worked')]: record.days_worked || 0,
    [t('Hours Worked')]: record.hours_worked || 0,
    [t('Overtime Hours')]: record.overtime_hours || 0,
    [t('Gross Pay')]: parseFloat(record.gross_pay || 0).toFixed(2),
    [t('Taxes')]: parseFloat(record.tax_amount || 0).toFixed(2),
    [t('Deductions')]: parseFloat(record.total_deductions || 0).toFixed(2),
    [t('Net Pay')]: parseFloat(record.net_pay || 0).toFixed(2),
    [t('Currency')]: record.currency || 'USD',
    [t('Status')]: record.status || '',
    [t('Payment Date')]: record.payment_date || ''
  }));
  
  return exportToExcel(data, 'payroll', t('Payroll'));
};

/**
 * Export employees to Excel
 */
export const exportEmployeesToExcel = (employeesData, language = 'en') => {
  if (!employeesData || employeesData.length === 0) {
    return false;
  }

  const t = makeT(language);
  const data = employeesData.map(emp => ({
    [t('ID')]: emp.id || '',
    [t('First Name')]: emp.first_name || '',
    [t('Last Name')]: emp.last_name || '',
    [t('Email')]: emp.email || '',
    [t('Phone')]: emp.phone || '',
    [t('Position')]: emp.position || '',
    [t('Department')]: emp.department || '',
    [t('Hire Date')]: emp.hire_date || '',
    [t('Payment Type')]: emp.payment_type || '',
    [t('Salary/Rate')]: emp.payment_type === 'hourly' 
      ? (emp.hourly_rate || 0) 
      : (emp.monthly_salary || 0),
    [t('Status')]: emp.status || ''
  }));
  
  return exportToExcel(data, 'employees', t('Employees'));
};

/**
 * Export finance records to Excel
 */
export const exportFinanceToExcel = (financeData, type = 'expenses', language = 'en') => {
  if (!financeData || financeData.length === 0) {
    return false;
  }

  const t = makeT(language);
  const data = financeData.map(record => ({
    [t('Date')]: record.date || record.transaction_date || '',
    [t('Category')]: record.category || '',
    [t('Description')]: record.description || '',
    [t('Amount')]: parseFloat(record.amount || 0).toFixed(2),
    [t('Currency')]: record.currency || 'USD',
    [t('Status')]: record.status || '',
    [t('Department')]: record.department || '-',
    [t('Created By')]: record.created_by_name || '-',
    [t('Notes')]: record.notes || ''
  }));
  
  const filename = type === 'expenses' ? 'expenses' : 'income';
  const sheetName = type === 'expenses' ? t('Expenses') : t('Income');
  return exportToExcel(data, filename, sheetName);
};

/**
 * Export contracts to Excel
 */
export const exportContractsToExcel = (contractsData, language = 'en') => {
  if (!contractsData || contractsData.length === 0) {
    return false;
  }

  const t = makeT(language);
  const data = contractsData.map(contract => ({
    [t('ID')]: contract.id || '',
    [t('Employee')]: `${contract.first_name || ''} ${contract.last_name || ''}`.trim(),
    [t('Type')]: contract.contract_type || '',
    [t('Start Date')]: contract.start_date || '',
    [t('End Date')]: contract.end_date || t('Indefinite'),
    [t('Salary')]: parseFloat(contract.salary || 0).toFixed(2),
    [t('Currency')]: contract.currency || 'USD',
    [t('Status')]: contract.status || '',
    [t('Signed')]: contract.signed_at ? t('Yes') : t('No'),
    [t('Signature Date')]: contract.signed_at || '-'
  }));
  
  return exportToExcel(data, 'contracts', t('Contracts'));
};

/**
 * Export benefits to Excel
 */
export const exportBenefitsToExcel = (benefitsData, language = 'en') => {
  if (!benefitsData || benefitsData.length === 0) {
    return false;
  }

  const t = makeT(language);
  const data = benefitsData.map(benefit => ({
    [t('Employee')]: `${benefit.first_name || ''} ${benefit.last_name || ''}`.trim(),
    [t('Benefit Type')]: benefit.benefit_name || benefit.benefit_type || '',
    [t('Description')]: benefit.description || '',
    [t('Value')]: parseFloat(benefit.value || 0).toFixed(2),
    [t('Frequency')]: benefit.frequency || '',
    [t('Start Date')]: benefit.start_date || '',
    [t('End Date')]: benefit.end_date || t('Ongoing'),
    [t('Status')]: benefit.status || ''
  }));
  
  return exportToExcel(data, 'benefits', t('Benefits'));
};

/**
 * Export documents to Excel
 */
export const exportDocumentsToExcel = (documentsData, language = 'en') => {
  if (!documentsData || documentsData.length === 0) {
    return false;
  }

  const t = makeT(language);
  const data = documentsData.map(doc => ({
    [t('ID')]: doc.id || '',
    [t('Name')]: doc.name || '',
    [t('Category')]: doc.category || '',
    [t('Employee')]: doc.employee_id 
      ? `${doc.first_name || ''} ${doc.last_name || ''}`.trim() 
      : t('General'),
    [t('File Type')]: doc.file_type || '',
    [t('Size (KB)')]: doc.file_size ? (doc.file_size / 1024).toFixed(2) : '',
    [t('Upload Date')]: doc.uploaded_at || doc.created_at || '',
    [t('Uploaded By')]: doc.uploaded_by_name || '-'
  }));
  
  return exportToExcel(data, 'documents', t('Documents'));
};

/**
 * Export leaves/absences to Excel
 */
export const exportLeavesToExcel = (leavesData, language = 'en') => {
  if (!leavesData || leavesData.length === 0) {
    return false;
  }

  const t = makeT(language);
  const data = leavesData.map(leave => ({
    [t('Employee')]: `${leave.first_name || ''} ${leave.last_name || ''}`.trim(),
    [t('Type')]: leave.leave_type || '',
    [t('Start Date')]: leave.start_date || '',
    [t('End Date')]: leave.end_date || '',
    [t('Days')]: leave.days || 0,
    [t('Reason')]: leave.reason || '',
    [t('Status')]: leave.status || '',
    [t('Requested On')]: leave.created_at || '',
    [t('Approved By')]: leave.approved_by_name || '-',
    [t('Approval Date')]: leave.approved_at || '-'
  }));
  
  return exportToExcel(data, 'leaves', t('Leaves'));
};

/**
 * Export users to Excel
 */
export const exportUsersToExcel = (usersData, language = 'en') => {
  if (!usersData || usersData.length === 0) {
    return false;
  }

  const t = makeT(language);
  const data = usersData.map(user => ({
    [t('ID')]: user.id || '',
    [t('First Name')]: user.first_name || '',
    [t('Last Name')]: user.last_name || '',
    [t('Email')]: user.email || '',
    [t('Role')]: user.role || '',
    [t('Status')]: user.status || '',
    [t('Last Login')]: user.last_login || '-',
    [t('Created At')]: user.created_at || ''
  }));
  
  return exportToExcel(data, 'users', t('Users'));
};

/**
 * Generic function to export any data
 */
export const exportGenericToExcel = (data, filename, sheetName) => {
  return exportToExcel(data, filename, sheetName);
};

/**
 * Export available payrolls to Excel
 */
export const exportAvailablePayrollsToExcel = (payrollsData, language = 'en') => {
  if (!payrollsData || payrollsData.length === 0) {
    return false;
  }

  const t = makeT(language);
  const data = payrollsData.map(record => ({
    [t('Employee')]: `${record.first_name || ''} ${record.last_name || ''}`.trim(),
    [t('Department')]: record.department || '',
    [t('Position')]: record.position || '',
    [t('Period Start')]: record.period_start || '',
    [t('Period End')]: record.period_end || '',
    [t('Gross Pay')]: parseFloat(record.gross_pay || 0).toFixed(2),
    [t('Deductions')]: parseFloat(record.total_deductions || 0).toFixed(2),
    [t('Net Pay')]: parseFloat(record.net_pay || 0).toFixed(2),
    [t('Status')]: record.status || '',
    [t('Created')]: record.created_at || ''
  }));
  
  return exportToExcel(data, 'available_payrolls', t('Available Payrolls'));
};

/**
 * Export consolidations to Excel
 */
export const exportConsolidationsToExcel = (consolidationsData, language = 'en') => {
  if (!consolidationsData || consolidationsData.length === 0) {
    return false;
  }

  const t = makeT(language);
  const data = consolidationsData.map(cons => ({
    [t('Code')]: cons.consolidation_code || '',
    [t('Date')]: cons.consolidation_date || '',
    [t('Period Start')]: cons.period_start || '',
    [t('Period End')]: cons.period_end || '',
    [t('Employees')]: cons.employee_count || 0,
    [t('Payroll Count')]: cons.payroll_count || 0,
    [t('Total Gross')]: parseFloat(cons.total_gross || 0).toFixed(2),
    [t('Total Deductions')]: parseFloat(cons.total_deductions || 0).toFixed(2),
    [t('Total Net')]: parseFloat(cons.total_net || 0).toFixed(2),
    [t('Status')]: cons.status || '',
    [t('Payment Date')]: cons.payment_date || '-',
    [t('Created By')]: cons.created_by_email || '',
    [t('Created At')]: cons.created_at || ''
  }));
  
  return exportToExcel(data, 'consolidations', t('Consolidations'));
};

/**
 * Export consolidation details to Excel
 */
export const exportConsolidationDetailsToExcel = (consolidation, items, language = 'en') => {
  if (!items || items.length === 0) {
    return false;
  }

  const t = makeT(language);
  const data = items.map(item => ({
    [t('Consolidation Code')]: consolidation.consolidation_code || '',
    [t('Employee')]: `${item.first_name || ''} ${item.last_name || ''}`.trim(),
    [t('Department')]: item.department || '',
    [t('Position')]: item.position || '',
    [t('Period Start')]: item.period_start || '',
    [t('Period End')]: item.period_end || '',
    [t('Gross Pay')]: parseFloat(item.gross_pay || 0).toFixed(2),
    [t('Deductions')]: parseFloat(item.total_deductions || 0).toFixed(2),
    [t('Net Pay')]: parseFloat(item.net_pay || 0).toFixed(2),
    [t('Payroll Status')]: item.payroll_status || ''
  }));
  
  return exportToExcel(
    data, 
    `consolidation_${consolidation.consolidation_code}_details`, 
    t('Details')
  );
};